import React, { useState, useEffect } from 'react';
import { roomsApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import {
  BedDouble,
  Plus,
  Search,
  Grid3X3,
  List,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

const statusConfig = {
  AVAILABLE: { bg: 'bg-success', text: 'text-success', icon: CheckCircle2, label: 'Available' },
  OUT_OF_ORDER: { bg: 'bg-destructive', text: 'text-destructive', icon: XCircle, label: 'Out of Order' },
  OUT_OF_SERVICE: { bg: 'bg-warning', text: 'text-warning', icon: AlertTriangle, label: 'Out of Service' },
};

export default function RoomsPage() {
  const { currentProperty, hasPermission } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [view, setView] = useState('grid');
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [roomForm, setRoomForm] = useState({
    room_number: '',
    room_type_id: '',
    floor: '',
    wing: '',
  });

  const [typeForm, setTypeForm] = useState({
    code: '',
    name: '',
    description: '',
    base_occupancy: 2,
    max_occupancy: 2,
    max_adults: 2,
    max_children: 0,
    bed_type: '',
    view_type: '',
  });

  useEffect(() => {
    if (currentProperty?.id) {
      loadData();
    }
  }, [currentProperty]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [roomsRes, typesRes] = await Promise.all([
        roomsApi.list(currentProperty.id),
        roomsApi.listTypes(currentProperty.id),
      ]);
      setRooms(roomsRes.data);
      setRoomTypes(typesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await roomsApi.create(currentProperty.id, roomForm);
      setIsRoomDialogOpen(false);
      setRoomForm({ room_number: '', room_type_id: '', floor: '', wing: '' });
      loadData();
    } catch (error) {
      console.error('Failed to create room:', error);
      alert(error.response?.data?.detail || 'Failed to create room');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await roomsApi.createType(currentProperty.id, typeForm);
      setIsTypeDialogOpen(false);
      setTypeForm({
        code: '',
        name: '',
        description: '',
        base_occupancy: 2,
        max_occupancy: 2,
        max_adults: 2,
        max_children: 0,
        bed_type: '',
        view_type: '',
      });
      loadData();
    } catch (error) {
      console.error('Failed to create room type:', error);
      alert(error.response?.data?.detail || 'Failed to create room type');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (roomId, newStatus) => {
    try {
      await roomsApi.updateStatus(roomId, newStatus);
      loadData();
    } catch (error) {
      console.error('Failed to update room status:', error);
      alert(error.response?.data?.detail || 'Failed to update room status');
    }
  };

  const filteredRooms = rooms.filter((room) => {
    const matchesSearch = room.room_number.toLowerCase().includes(search.toLowerCase());
    const matchesType = !filterType || room.room_type_id === filterType;
    const matchesStatus = !filterStatus || room.maintenance_status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Group rooms by floor
  const roomsByFloor = filteredRooms.reduce((acc, room) => {
    const floor = room.floor || 'Unknown';
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(room);
    return acc;
  }, {});

  if (!currentProperty) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <BedDouble className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">Please select a property first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-admin-heading)' }}>
            Rooms
          </h1>
          <p className="text-muted-foreground">
            Manage rooms for {currentProperty.name}
          </p>
        </div>

        <div className="flex gap-2">
          {hasPermission('room:create') && (
            <>
              <Dialog open={isTypeDialogOpen} onOpenChange={setIsTypeDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-room-type-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Type
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Room Type</DialogTitle>
                    <DialogDescription>Create a new room category</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateType} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="type-code">Code</Label>
                        <Input
                          id="type-code"
                          value={typeForm.code}
                          onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value.toUpperCase() })}
                          placeholder="STD"
                          required
                          data-testid="room-type-code-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="type-name">Name</Label>
                        <Input
                          id="type-name"
                          value={typeForm.name}
                          onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                          placeholder="Standard Room"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Max Adults</Label>
                        <Input
                          type="number"
                          value={typeForm.max_adults}
                          onChange={(e) => setTypeForm({ ...typeForm, max_adults: parseInt(e.target.value) || 0 })}
                          min={1}
                          max={10}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Children</Label>
                        <Input
                          type="number"
                          value={typeForm.max_children}
                          onChange={(e) => setTypeForm({ ...typeForm, max_children: parseInt(e.target.value) || 0 })}
                          min={0}
                          max={10}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Bed Type</Label>
                        <Select
                          value={typeForm.bed_type}
                          onValueChange={(value) => setTypeForm({ ...typeForm, bed_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="KING">King</SelectItem>
                            <SelectItem value="QUEEN">Queen</SelectItem>
                            <SelectItem value="TWIN">Twin</SelectItem>
                            <SelectItem value="DOUBLE">Double</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>View Type</Label>
                        <Select
                          value={typeForm.view_type}
                          onValueChange={(value) => setTypeForm({ ...typeForm, view_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CITY">City</SelectItem>
                            <SelectItem value="GARDEN">Garden</SelectItem>
                            <SelectItem value="POOL">Pool</SelectItem>
                            <SelectItem value="SEA">Sea</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsTypeDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={saving}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={isRoomDialogOpen} onOpenChange={setIsRoomDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-room-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Room
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Room</DialogTitle>
                    <DialogDescription>Create a new room in this property</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateRoom} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="room-number">Room Number</Label>
                        <Input
                          id="room-number"
                          value={roomForm.room_number}
                          onChange={(e) => setRoomForm({ ...roomForm, room_number: e.target.value })}
                          placeholder="101"
                          required
                          data-testid="room-number-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="floor">Floor</Label>
                        <Input
                          id="floor"
                          value={roomForm.floor}
                          onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}
                          placeholder="1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Room Type</Label>
                      <Select
                        value={roomForm.room_type_id}
                        onValueChange={(value) => setRoomForm({ ...roomForm, room_type_id: value })}
                      >
                        <SelectTrigger data-testid="room-type-select">
                          <SelectValue placeholder="Select room type" />
                        </SelectTrigger>
                        <SelectContent>
                          {roomTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name} ({type.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsRoomDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={saving || !roomForm.room_type_id} data-testid="save-room-button">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Room'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Tabs for Room Types vs Rooms */}
      <Tabs defaultValue="rooms">
        <TabsList>
          <TabsTrigger value="rooms">
            <BedDouble className="h-4 w-4 mr-2" />
            Rooms ({rooms.length})
          </TabsTrigger>
          <TabsTrigger value="types">
            <Grid3X3 className="h-4 w-4 mr-2" />
            Room Types ({roomTypes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rooms" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search rooms..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterType} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {roomTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="AVAILABLE">Available</SelectItem>
                <SelectItem value="OUT_OF_ORDER">Out of Order</SelectItem>
                <SelectItem value="OUT_OF_SERVICE">Out of Service</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex border rounded-lg">
              <Button
                variant={view === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setView('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={view === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setView('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Rooms Display */}
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : filteredRooms.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <BedDouble className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No rooms found</p>
              </CardContent>
            </Card>
          ) : (
            Object.entries(roomsByFloor).sort().map(([floor, floorRooms]) => (
              <div key={floor}>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Floor {floor}</h3>
                <div className={cn(
                  view === 'grid' ? 'grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3' : 'space-y-2'
                )}>
                  {floorRooms.map((room) => {
                    const config = statusConfig[room.maintenance_status];
                    const StatusIcon = config?.icon || CheckCircle2;

                    return view === 'grid' ? (
                      <Card
                        key={room.id}
                        className={cn(
                          'cursor-pointer hover:shadow-md transition-shadow',
                          room.maintenance_status !== 'AVAILABLE' && 'opacity-60'
                        )}
                        data-testid={`room-${room.room_number}`}
                      >
                        <CardContent className="p-3 text-center">
                          <div className={cn('w-3 h-3 rounded-full mx-auto mb-2', config?.bg)} />
                          <div className="font-bold text-lg">{room.room_number}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {room.room_type?.name || room.room_type?.code}
                          </div>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card key={room.id} className="p-3" data-testid={`room-${room.room_number}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={cn('w-3 h-3 rounded-full', config?.bg)} />
                            <div>
                              <span className="font-medium">{room.room_number}</span>
                              <span className="text-muted-foreground mx-2">-</span>
                              <span className="text-sm text-muted-foreground">
                                {room.room_type?.name}
                              </span>
                            </div>
                          </div>
                          <Badge variant="outline" className={config?.text}>
                            {config?.label}
                          </Badge>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="types" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roomTypes.map((type) => (
              <Card key={type.id} data-testid={`room-type-${type.code}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{type.name}</CardTitle>
                      <CardDescription className="font-mono text-xs">{type.code}</CardDescription>
                    </div>
                    <Badge variant={type.status === 'ACTIVE' ? 'default' : 'secondary'}>
                      {type.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Max Occupancy</span>
                    <span>{type.max_adults} Adults + {type.max_children} Children</span>
                  </div>
                  {type.bed_type && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Bed Type</span>
                      <span className="capitalize">{type.bed_type?.toLowerCase()}</span>
                    </div>
                  )}
                  {type.view_type && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">View</span>
                      <span className="capitalize">{type.view_type?.toLowerCase()}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
